import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon, EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import { FaLinkedin, FaMapMarkerAlt, FaRocket } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import logo from "../../logo.png";
import { SERVICES } from "./config";

const companyLinks = [
  { name: "About", path: "/about" },
  { name: "Technologies", path: "/technologies" },
  { name: "Portfolio", path: "/portfolio" },
  { name: "Careers", path: "/careers" },
];

export default function Footer() {
  const bgColor = "rgba(8, 13, 24, 0.96)";
  const cardBg = "rgba(255, 255, 255, 0.045)";
  const textColor = "#F4F4F6";
  const textMuted = "#A0AEC0";
  const accent = "#FF6F20";
  const accentCyan = "#00FFD0";
  const border = "rgba(255, 255, 255, 0.1)";

  return (
    <Box
      as="footer"
      bg={bgColor}
      color={textColor}
      borderTop="1px solid rgba(0, 255, 208, 0.18)"
      boxShadow="0 -18px 50px rgba(0, 0, 0, 0.28)"
    >
      <Box maxW="1440px" mx="auto" px={{ base: 5, md: 8 }} py={{ base: 10, md: 12 }}>
        <Flex
          align={{ base: "start", lg: "center" }}
          justify="space-between"
          gap={6}
          direction={{ base: "column", lg: "row" }}
          bg={cardBg}
          border={`1px solid ${border}`}
          borderRadius="xl"
          p={{ base: 5, md: 6 }}
          mb={10}
        >
          <HStack spacing={4} align="center">
            <Flex
              align="center"
              justify="center"
              w={12}
              h={12}
              borderRadius="lg"
              bg="rgba(0, 255, 208, 0.1)"
              border="1px solid rgba(0, 255, 208, 0.28)"
              flexShrink={0}
            >
              <Icon as={FaRocket} color={accentCyan} boxSize={6} />
            </Flex>
            <Box>
              <Heading size="md" mb={1}>
                Ready to shape the next build?
              </Heading>
              <Text color={textMuted}>
                Share the goal, workflow, or product idea. We will help map the next step.
              </Text>
            </Box>
          </HStack>
          <Button
            as={RouterLink}
            to="/contact"
            bg={accent}
            color="white"
            rightIcon={<ArrowForwardIcon />}
            borderRadius="lg"
            px={6}
            flexShrink={0}
            _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}
          >
            Start Project
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 8, lg: 10 }}>
          <VStack align="start" spacing={4}>
            <Link as={RouterLink} to="/" _hover={{ textDecoration: "none" }}>
              <Image
                src={logo}
                alt="Orvitrix Logo"
                objectFit="contain"
                w="122px"
                h="66px"
                filter="drop-shadow(0 0 10px rgba(255, 111, 32, 0.38))"
              />
            </Link>
            <Text color={textMuted} lineHeight="1.7" maxW="290px">
              Product engineering, automation, data workflows, and cloud-ready
              systems for teams building with intent.
            </Text>
            <HStack spacing={3}>
              <Link
                href="https://www.linkedin.com/company/orvitrix-pvt-ltd"
                isExternal
                aria-label="Orvitrix LinkedIn"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w={10}
                h={10}
                borderRadius="lg"
                bg={cardBg}
                border={`1px solid ${border}`}
                _hover={{ color: accentCyan, borderColor: "rgba(0, 255, 208, 0.42)" }}
              >
                <Icon as={FaLinkedin} boxSize={5} color="#0A66C2" />
              </Link>
            </HStack>
          </VStack>

          <FooterColumn title="Services" accent={accentCyan}>
            {SERVICES.slice(0, 6).map((service) => (
              <FooterLink key={service.path} to={service.path}>
                {service.name}
              </FooterLink>
            ))}
            <FooterLink to="/services">View all services</FooterLink>
          </FooterColumn>

          <FooterColumn title="Company" accent={accentCyan}>
            {companyLinks.map((item) => (
              <FooterLink key={item.path} to={item.path}>
                {item.name}
              </FooterLink>
            ))}
          </FooterColumn>

          <Box>
            <Heading
              size="xs"
              mb={4}
              color={accentCyan}
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Contact
            </Heading>
            <Stack spacing={4} color={textMuted}>
              <HStack align="start" spacing={3}>
                <EmailIcon color={accent} mt="3px" />
                <Box>
                  <Text color={textColor} fontWeight="bold" fontSize="sm">
                    General
                  </Text>
                  <Link href="mailto:info@orvitrix.com" aria-label="Email Orvitrix at info@orvitrix.com" _hover={{ color: accentCyan }}>
                    info@orvitrix.com
                  </Link>
                </Box>
              </HStack>

              <HStack align="start" spacing={3}>
                <EmailIcon color={accent} mt="3px" />
                <Box>
                  <Text color={textColor} fontWeight="bold" fontSize="sm">
                    Careers
                  </Text>
                  <Link href="mailto:careers@orvitrix.com" _hover={{ color: accentCyan }}>
                    careers@orvitrix.com
                  </Link>
                </Box>
              </HStack>
              <HStack align="start" spacing={3}>
                <PhoneIcon color={accent} mt="3px" />
                <Box>
                  <Text color={textColor} fontWeight="bold" fontSize="sm">
                    Phone
                  </Text>
                  <Link href="tel:+919901975001" _hover={{ color: accentCyan }}>
                    +91 99019 75001
                  </Link>
                </Box>
              </HStack>
              <HStack align="start" spacing={3}>
                <Icon as={FaMapMarkerAlt} color={accent} mt="3px" />
                <Box>
                  <Text color={textColor} fontWeight="bold" fontSize="sm">
                    Location
                  </Text>
                  <Text>Delhi, India</Text>
                </Box>
              </HStack>
            </Stack>
          </Box>
        </SimpleGrid>

        <Divider my={8} borderColor={border} />

        <Flex
          align={{ base: "start", md: "center" }}
          justify="space-between"
          gap={3}
          direction={{ base: "column", md: "row" }}
          color={textMuted}
          fontSize="sm"
        >
          <Text>© 2025-{new Date().getFullYear()} Orvitrix Private Limited. All rights reserved.</Text>
          <Text>Built for practical digital products.</Text>
        </Flex>
      </Box>
    </Box>
  );
}

function FooterColumn({ title, accent, children }) {
  return (
    <Box>
      <Heading
        size="xs"
        mb={4}
        color={accent}
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {title}
      </Heading>
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}

function FooterLink({ to, children }) {
  return (
    <Link
      as={RouterLink}
      to={to}
      color="#A0AEC0"
      fontSize="sm"
      _hover={{
        color: "#00FFD0",
        textDecoration: "none",
        transform: "translateX(3px)",
      }}
      transition="all 0.2s"
    >
      {children}
    </Link>
  );
}
